import React, { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HandHeart, Plus, X, MessageCircle, Trash2, Sparkles, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PrayerPraise() {
  const [tab, setTab] = useState("prayer");
  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs tracking-[0.2em] uppercase text-[#8C6A54] font-semibold">Bear One Another's Burdens</p>
        <h1 className="font-heading text-5xl text-[#2A2621] mt-2">Prayer &amp; Praise</h1>
        <p className="text-[#615A50] mt-2">"Pray one for another... The effectual fervent prayer of a righteous man availeth much." (James 5:16, KJV)</p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab("prayer")} data-testid="tab-prayer"
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "prayer" ? "bg-[#3D5844] text-white" : "border border-[#D5CEBF] text-[#615A50] hover:bg-[#F3EFE4]"}`}>Prayer Requests</button>
        <button onClick={() => setTab("praise")} data-testid="tab-praise"
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "praise" ? "bg-[#3D5844] text-white" : "border border-[#D5CEBF] text-[#615A50] hover:bg-[#F3EFE4]"}`}>Praise Reports</button>
      </div>

      {tab === "prayer" ? <PrayerWall /> : <PraiseWall />}
    </div>
  );
}

function PrayerWall() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", anonymous: false });

  const load = () => api.get("/prayers").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/prayers", form);
      toast.success("Your request has been shared. We're praying with you.");
      setShowForm(false); setForm({ title: "", body: "", anonymous: false }); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  return (
    <div className="space-y-5">
      <button onClick={() => setShowForm(true)} data-testid="prayer-new"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C46B4E] text-white font-medium hover:bg-[#AC5B40] transition-colors">
        <Plus className="w-4 h-4" /> Share a prayer request
      </button>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((p) => <PrayerCard key={p.id} p={p} user={user} reload={load} />)}
        {items.length === 0 && <p className="text-[#615A50]">No prayer requests yet. Be the first to share what's on your heart.</p>}
      </div>

      {showForm && (
        <Modal title="Share a prayer request" onClose={() => setShowForm(false)}>
          <form onSubmit={create} className="space-y-4">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Healing for my mother)" data-testid="prayer-title"
              className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
            <textarea required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Share your heart…" rows={4} data-testid="prayer-body"
              className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" />
            <label className="flex items-center gap-2 text-sm text-[#615A50]">
              <input type="checkbox" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} data-testid="prayer-anon" className="accent-[#3D5844] w-4 h-4" />
              Post anonymously
            </label>
            <button type="submit" data-testid="prayer-submit" className="w-full rounded-xl bg-[#3D5844] text-white py-3 font-medium hover:bg-[#2F4535] transition-colors">Share request</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function PrayerCard({ p, user, reload }) {
  const [state, setState] = useState({ i_prayed: p.i_prayed, pray_count: p.pray_count, answered: p.answered });
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  const pray = async () => {
    try { const { data } = await api.post(`/prayers/${p.id}/pray`); setState((s) => ({ ...s, ...data })); }
    catch (e) { toast.error(apiError(e)); }
  };
  const loadComments = async () => {
    const { data } = await api.get(`/prayers/${p.id}/comments`); setComments(data);
  };
  const toggleComments = async () => { if (!open) await loadComments(); setOpen(!open); };
  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/prayers/${p.id}/comments`, { body: text }); setText(""); loadComments(); }
    catch (e2) { toast.error(apiError(e2)); }
  };
  const answered = async () => {
    try { const { data } = await api.post(`/prayers/${p.id}/answered`); setState((s) => ({ ...s, answered: data.answered })); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this prayer request?")) return;
    try { await api.delete(`/prayers/${p.id}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  const mine = p.is_mine || user?.role === "admin";

  return (
    <div className={`rounded-2xl border p-6 ${state.answered ? "border-[#3D5844]/40 bg-[#3D5844]/5" : "border-[#D5CEBF] bg-[#F3EFE4]"}`} data-testid={`prayer-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-heading text-2xl text-[#2A2621]">{p.title}</h3>
        {mine && <button onClick={remove} data-testid={`prayer-delete-${p.id}`} className="text-[#615A50] hover:text-[#AC5B40] shrink-0"><Trash2 className="w-4 h-4" /></button>}
      </div>
      {state.answered && <span className="inline-flex items-center gap-1 text-xs text-[#3D5844] font-semibold mt-1"><CheckCircle2 className="w-3.5 h-3.5" /> Answered — Praise God!</span>}
      <p className="text-[#2A2621] mt-2 leading-relaxed whitespace-pre-wrap">{p.body}</p>
      <p className="text-xs text-[#615A50] mt-3">— {p.author_name}</p>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button onClick={pray} data-testid={`prayer-pray-${p.id}`}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${state.i_prayed ? "bg-[#C46B4E] text-white" : "border border-[#D5CEBF] text-[#2A2621] hover:bg-[#E7DFCE]"}`}>
          <HandHeart className="w-4 h-4" /> {state.i_prayed ? "Prayed" : "I prayed"} · {state.pray_count}
        </button>
        <button onClick={toggleComments} data-testid={`prayer-comments-${p.id}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border border-[#D5CEBF] text-[#2A2621] hover:bg-[#E7DFCE]">
          <MessageCircle className="w-4 h-4" /> {p.comment_count}
        </button>
        {mine && (
          <button onClick={answered} data-testid={`prayer-answered-${p.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border border-[#D5CEBF] text-[#3D5844] hover:bg-[#E7DFCE]">
            <CheckCircle2 className="w-4 h-4" /> {state.answered ? "Unmark" : "Mark answered"}
          </button>
        )}
      </div>

      {open && (
        <div className="mt-4 space-y-3 border-t border-[#D5CEBF] pt-4">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              {fileUrl(c.author_picture) ? <img src={fileUrl(c.author_picture)} alt="" className="w-7 h-7 rounded-full object-cover" /> :
                <span className="w-7 h-7 rounded-full bg-[#3D5844] text-white flex items-center justify-center text-[11px]">{c.author_name?.charAt(0)}</span>}
              <div className="bg-[#FAF7EE] rounded-xl border border-[#D5CEBF] px-3 py-2 flex-1">
                <p className="text-xs font-medium text-[#2A2621]">{c.author_name}</p>
                <p className="text-sm text-[#2A2621]">{c.body}</p>
              </div>
            </div>
          ))}
          <form onSubmit={addComment} className="flex items-center gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write an encouragement…" data-testid={`prayer-comment-input-${p.id}`}
              className="flex-1 rounded-full border border-[#D5CEBF] bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
            <button type="submit" className="w-9 h-9 rounded-full bg-[#3D5844] text-white flex items-center justify-center"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </div>
  );
}

function PraiseWall() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });

  const load = () => api.get("/testimonies").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/testimonies", form); toast.success("Praise God! Thank you for sharing."); setShowForm(false); setForm({ title: "", body: "" }); load(); }
    catch (e2) { toast.error(apiError(e2)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this praise report?")) return;
    try { await api.delete(`/testimonies/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      <button onClick={() => setShowForm(true)} data-testid="praise-new"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C46B4E] text-white font-medium hover:bg-[#AC5B40] transition-colors">
        <Sparkles className="w-4 h-4" /> Share a praise report
      </button>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((t) => (
          <div key={t.id} className="rounded-2xl border border-[#D89A8C]/40 bg-[#F9ECE8] p-6" data-testid={`praise-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-heading text-2xl text-[#2A2621]">{t.title}</h3>
              {(t.author_id === user?.user_id || user?.role === "admin") &&
                <button onClick={() => remove(t.id)} className="text-[#615A50] hover:text-[#AC5B40] shrink-0"><Trash2 className="w-4 h-4" /></button>}
            </div>
            <p className="text-[#2A2621] mt-2 leading-relaxed whitespace-pre-wrap">{t.body}</p>
            <p className="text-xs text-[#615A50] mt-3">— {t.author_name}</p>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#615A50]">No praise reports yet. Share how God has moved!</p>}
      </div>

      {showForm && (
        <Modal title="Share a praise report" onClose={() => setShowForm(false)}>
          <form onSubmit={create} className="space-y-4">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="praise-title"
              className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
            <textarea required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Tell us how God has answered prayer or blessed you…" rows={4} data-testid="praise-body"
              className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" />
            <button type="submit" data-testid="praise-submit" className="w-full rounded-xl bg-[#3D5844] text-white py-3 font-medium hover:bg-[#2F4535] transition-colors">Share praise</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#2A2621]/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF7EE] rounded-2xl border border-[#D5CEBF] w-full max-w-lg p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-3xl text-[#2A2621]">{title}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-[#615A50]" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
